// src/components/locations/forms/CityForm.tsx
import React from "react";
import { Link } from "react-router-dom";
import { FiPlus } from "react-icons/fi";

import type {
  LocationCity,
  LocationCountry,
  LocationRegion,
} from "@/api/features/locations/types";

import {
  AdvancedSection,
  FieldRow,
  FormSection,
} from "@/pages/locations/components/form/FormSection";
import { FormField } from "@/pages/locations/components/form/FormField";
import { controlClass } from "@/pages/locations/components/form/controlClass";
import {
  FormActions,
  FormError,
} from "@/pages/locations/components/form/FormActions";
import { CountryPicker } from "@/pages/locations/components/CountryPicker";
import { RecordMeta } from "@/pages/locations/components/form/RecordMeta";
import { useLocationForm } from "@/pages/locations/components/form/useLocationForm";
import {
  apiErrorSummary,
  collectErrors,
  coordinateRule,
  requiredText,
} from "@/pages/locations/formErrors";

export type CityFormValues = {
  countryIso2: string;
  /** "" means the city has no region. */
  regionId: string;
  name: string;
  nameNorm: string;
  latitude: string;
  longitude: string;
};

type Props = {
  mode: "create" | "edit";
  value?: LocationCity;
  countries: LocationCountry[];
  countriesLoading?: boolean;
  regions: LocationRegion[];
  regionsLoading?: boolean;
  defaultCountryIso2?: string;
  defaultRegionId?: string;
  /** Lets the page reload regions when the country changes. */
  onCountryChange?: (iso2: string) => void;
  /** Where "Add a region" goes when the country has none yet. */
  addRegionHref?: string;
  isSubmitting: boolean;
  error?: unknown;
  onSubmit: (values: CityFormValues, options: { addAnother: boolean }) => void;
  onCancel: (isDirty: boolean) => void;
  resetSignal?: number;
};

function derivedAlias(name: string): string {
  return name.trim().toLowerCase();
}

const CityForm: React.FC<Props> = ({
  mode,
  value,
  countries,
  countriesLoading = false,
  regions,
  regionsLoading = false,
  defaultCountryIso2 = "",
  defaultRegionId = "",
  onCountryChange,
  addRegionHref,
  isSubmitting,
  error,
  onSubmit,
  onCancel,
  resetSignal = 0,
}) => {
  const initial = React.useMemo<CityFormValues>(
    () => ({
      countryIso2: String(
        value?.countryIso2 ?? defaultCountryIso2 ?? ""
      ).toUpperCase(),
      regionId:
        value?.regionId != null
          ? String(value.regionId)
          : value
            ? "" // the record genuinely has no region
            : defaultRegionId,
      name: value?.name ?? "",
      nameNorm: value?.nameNorm ?? "",
      latitude: value?.latitude != null ? String(value.latitude) : "",
      longitude: value?.longitude != null ? String(value.longitude) : "",
    }),
    [value, defaultCountryIso2, defaultRegionId]
  );

  const validate = React.useCallback(
    (candidate: CityFormValues) =>
      collectErrors({
        countryIso2: candidate.countryIso2
          ? undefined
          : "Pick the country this city is in.",
        name: requiredText(candidate.name, "City name"),
        latitude: coordinateRule(candidate.latitude, 90, "Latitude"),
        longitude: coordinateRule(candidate.longitude, 180, "Longitude"),
      }),
    []
  );

  const {
    values,
    setField,
    setFields,
    errorFor,
    isDirty,
    formRef,
    handleSubmit,
    resetTo,
  } = useLocationForm<CityFormValues>({
      initial,
      validate,
      idPrefix: "city",
      error,
      onValid: (v, options) =>
        onSubmit(
          {
            ...v,
            countryIso2: v.countryIso2.trim().toUpperCase(),
            name: v.name.trim(),
            nameNorm: v.nameNorm.trim(),
            latitude: v.latitude.trim(),
            longitude: v.longitude.trim(),
          },
          options
        ),
    });

  React.useEffect(() => {
    if (resetSignal > 0) {
      // Country and region carry over — cities are entered in batches.
      resetTo({
        countryIso2: values.countryIso2,
        regionId: values.regionId,
        name: "",
        nameNorm: "",
        latitude: "",
        longitude: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  const countryLocked = mode === "edit";
  const selectedCountry = countries.find((c) => c.iso2 === values.countryIso2);

  const noRegionsYet =
    Boolean(values.countryIso2) && !regionsLoading && regions.length === 0;

  return (
    <form
      ref={formRef}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(false);
      }}
    >
      <FormError message={apiErrorSummary(error)} />

      <FormSection
        title="Where it belongs"
        description="Every city sits under a country, and optionally under one of that country's regions."
      >
        <FormField
          id="city-countryIso2"
          label="Country"
          required
          error={errorFor("countryIso2")}
          hint={
            countryLocked
              ? "A city cannot be moved to another country — create it under the correct one instead."
              : "Start typing to filter the list."
          }
        >
          {(a) =>
            countryLocked ? (
              <div className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm text-gray-700">
                {selectedCountry?.name ?? values.countryIso2}
                <span className="rounded border border-gray-200 bg-white px-1.5 py-0.5 font-mono text-[11px] text-gray-500">
                  {values.countryIso2}
                </span>
              </div>
            ) : (
              <CountryPicker
                id={a.id}
                describedBy={a["aria-describedby"]}
                value={values.countryIso2}
                onChange={(iso2) => {
                  // Regions belong to a country, so the stale region has to be
                  // dropped in the same update as the country.
                  setFields({ countryIso2: iso2, regionId: "" });
                  onCountryChange?.(iso2);
                }}
                countries={countries}
                disabled={isSubmitting || countriesLoading}
                error={errorFor("countryIso2")}
              />
            )
          }
        </FormField>

        <FormField
          id="city-regionId"
          label="Region"
          error={errorFor("regionId")}
          hint={
            noRegionsYet
              ? undefined
              : "Optional. Leave as “No region” for city-states and territories."
          }
        >
          {(a) => (
            <select
              {...a}
              value={values.regionId}
              onChange={(e) => setField("regionId", e.target.value)}
              disabled={isSubmitting || !values.countryIso2 || regionsLoading}
              className={controlClass(errorFor("regionId"))}
            >
              <option value="">
                {regionsLoading ? "Loading regions…" : "No region"}
              </option>
              {regions.map((r) => (
                <option key={r.id} value={String(r.id)}>
                  {r.name}
                  {r.code ? ` (${r.code})` : ""}
                </option>
              ))}
            </select>
          )}
        </FormField>

        {noRegionsYet && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
            <span>
              {selectedCountry?.name ?? "This country"} has no regions yet — the
              city will be saved without one.
            </span>
            {addRegionHref && (
              <Link
                to={addRegionHref}
                className="inline-flex items-center gap-1 font-semibold text-amber-900 underline underline-offset-2"
              >
                <FiPlus />
                Add a region
              </Link>
            )}
          </div>
        )}
      </FormSection>

      <FormSection title="City details">
        <FormField
          id="city-name"
          label="City name"
          required
          error={errorFor("name")}
          hint="The name travellers would recognise, e.g. Mumbai."
        >
          {(a) => (
            <input
              {...a}
              value={values.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={isSubmitting}
              autoFocus
              placeholder="Mumbai"
              className={controlClass(errorFor("name"))}
            />
          )}
        </FormField>
      </FormSection>

      <AdvancedSection description="Search matching and map coordinates — safe to skip.">
        <FormField
          id="city-nameNorm"
          label="Search alias"
          error={errorFor("nameNorm")}
          hint={
            values.nameNorm.trim()
              ? "Used when matching this city against supplier and booking data."
              : `Leave blank and “${derivedAlias(values.name) || "the city name"}” is generated for you.`
          }
        >
          {(a) => (
            <input
              {...a}
              value={values.nameNorm}
              onChange={(e) => setField("nameNorm", e.target.value)}
              disabled={isSubmitting}
              placeholder={derivedAlias(values.name) || "mumbai"}
              className={controlClass(errorFor("nameNorm"))}
            />
          )}
        </FormField>

        <FieldRow>
          <FormField
            id="city-latitude"
            label="Latitude"
            error={errorFor("latitude")}
            hint="Between -90 and 90."
          >
            {(a) => (
              <input
                {...a}
                value={values.latitude}
                onChange={(e) => setField("latitude", e.target.value)}
                disabled={isSubmitting}
                inputMode="decimal"
                placeholder="19.0760"
                className={controlClass(errorFor("latitude"))}
              />
            )}
          </FormField>

          <FormField
            id="city-longitude"
            label="Longitude"
            error={errorFor("longitude")}
            hint="Between -180 and 180."
          >
            {(a) => (
              <input
                {...a}
                value={values.longitude}
                onChange={(e) => setField("longitude", e.target.value)}
                disabled={isSubmitting}
                inputMode="decimal"
                placeholder="72.8777"
                className={controlClass(errorFor("longitude"))}
              />
            )}
          </FormField>
        </FieldRow>
      </AdvancedSection>

      {mode === "edit" && value && <RecordMeta record={value} />}

      <FormActions
        submitLabel={mode === "create" ? "Create city" : "Save changes"}
        submittingLabel="Saving…"
        isSubmitting={isSubmitting}
        onCancel={() => onCancel(isDirty)}
        onSaveAndAddAnother={mode === "create" ? () => handleSubmit(true) : undefined}
      />
    </form>
  );
};

export default CityForm;
